
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FraudDetectionRequest, FraudDetectionResponse } from "@/utils/a2a/types/screeningTypes";

export interface FraudDetectionState {
  status: 'idle' | 'scanning' | 'completed' | 'failed';
  fraudScore: number | null;
  alerts: Array<{
    severity: 'low' | 'medium' | 'high' | 'critical';
    type: 'identity' | 'resume' | 'location' | 'behavior' | 'reference';
    description: string;
  }> | null;
  evidenceLinks: Array<{
    title: string;
    url: string;
    description: string;
  }> | null;
  recommendation: 'proceed' | 'additional_verification' | 'escalate' | 'reject' | null;
  confidenceScore: number | null;
}

export function useFraudDetection() {
  const [state, setState] = useState<FraudDetectionState>({
    status: 'idle',
    fraudScore: null,
    alerts: null,
    evidenceLinks: null,
    recommendation: null,
    confidenceScore: null
  });

  const runFraudDetection = async (request: FraudDetectionRequest): Promise<FraudDetectionResponse | null> => {
    try {
      setState(prev => ({ ...prev, status: 'scanning' }));
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Authentication required to use fraud detection");
      }
      
      // In a real implementation, this would call a Supabase edge function
      // For demo purposes, we'll simulate the process
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Generate a simulated response based on the candidate data
      // In a real implementation, this would come from AI analysis
      const fraudScore = Math.floor(Math.random() * 30); // Low risk for demo
      
      const alerts = fraudScore > 15 ? [
        {
          severity: 'low' as const,
          type: 'location' as const,
          description: 'IP address location does not match candidate\'s stated location'
        }
      ] : [];
      
      const evidenceLinks = [];
      
      if (fraudScore > 20) {
        evidenceLinks.push({
          title: 'Resume Verification',
          url: 'https://example.com/verification',
          description: 'Automated background check results'
        });
      }
      
      let recommendation: 'proceed' | 'additional_verification' | 'escalate' | 'reject';
      if (fraudScore < 15) {
        recommendation = 'proceed';
      } else if (fraudScore < 30) {
        recommendation = 'additional_verification';
      } else if (fraudScore < 70) {
        recommendation = 'escalate';
      } else {
        recommendation = 'reject';
      }
      
      const result: FraudDetectionResponse = {
        fraudScore,
        alerts,
        evidenceLinks,
        recommendation,
        confidenceScore: 0.85
      };
      
      setState({
        status: 'completed',
        fraudScore,
        alerts,
        evidenceLinks,
        recommendation,
        confidenceScore: 0.85
      });
      
      toast.success("Fraud detection scan completed");
      return result;
    } catch (err) {
      console.error("Error in fraud detection:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to run fraud detection";
      toast.error(errorMessage);
      
      setState(prev => ({ ...prev, status: 'failed' }));
      return null;
    }
  };

  return {
    fraudDetection: state,
    runFraudDetection
  };
}
